import React from 'react';
import { 
  ChartBarIcon, 
  DocumentIcon, 
  CircleStackIcon, 
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Dashboard: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  if (!isActive) return null;

  const stats = [

    {
      name: 'Data Files',
      value: '89',
      change: '+7',
      changeType: 'increase',
      icon: CircleStackIcon,
      color: 'text-purple-600'
    },
    {
      name: 'Metadata Files',
      value: '56',
      change: '+3',
      changeType: 'increase',
      icon: DocumentIcon,
      color: 'text-blue-600'
    },
    {
      name: 'Total Runs',
      value: '150',
      change: '+12%',
      changeType: 'increase',
      icon: CheckCircleIcon,
      color: 'text-green-600'
    },
    {
      name: 'Success Rate',
      value: '94.2%',
      change: '+2.1%',
      changeType: 'increase',
      icon: ChartBarIcon,
      color: 'text-green-600'
    }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'validation',
      message: 'Validation completed for employee_data.csv',
      time: '2 minutes ago',
      status: 'success'
    },
    {
      id: 2,
      type: 'upload',
      message: 'New metadata file uploaded: product_schema.json',
      time: '15 minutes ago',
      status: 'info'
    },
    {
      id: 3,
      type: 'validation',
      message: 'Validation failed for sales_data.csv',
      time: '1 hour ago',
      status: 'error'
    },
    {
      id: 4,
      type: 'validation',
      message: 'Validation completed for customer_info.csv',
      time: '2 hours ago',
      status: 'success'
    }
  ];

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Dashboard</h1>
          <p className="text-secondary-600">
            Overview of your data validation activities and system status
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600">{stat.name}</p>
                    <p className="text-2xl font-bold text-secondary-900 mt-1">{stat.value}</p>
                    <p className={`text-sm mt-1 ${
                      stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change} from last month
                    </p>
                  </div>
                  <Icon className={`w-8 h-8 ${stat.color}`} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <div className="lg:col-span-2 card p-6">
            <h2 className="text-xl font-semibold text-secondary-900 mb-6 flex items-center">
              <ClockIcon className="w-6 h-6 mr-2 text-primary-600" />
              Recent Activity
            </h2>
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-4 p-4 bg-secondary-50 rounded-lg">
                  <div className={`
                    w-2 h-2 rounded-full mt-2 flex-shrink-0
                    ${activity.status === 'success' ? 'bg-green-500' : 
                      activity.status === 'error' ? 'bg-red-500' : 'bg-blue-500'}
                  `} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-secondary-900">{activity.message}</p>
                    <p className="text-xs text-secondary-500 mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* System Status */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-secondary-900 mb-6">System Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-green-800">API Service</span>
                </div>
                <span className="text-xs text-green-600 font-medium">Online</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-green-800">Database</span>
                </div>
                <span className="text-xs text-green-600 font-medium">Connected</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-800">Queue</span>
                </div>
                <span className="text-xs text-yellow-600 font-medium">3 pending</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 pt-6 border-t border-secondary-200">
              <h3 className="text-sm font-medium text-secondary-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full text-left p-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200">
                  Run System Health Check
                </button>
                <button className="w-full text-left p-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200">
                  View Validation Logs
                </button>
                <button className="w-full text-left p-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200">
                  Export Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
